import { Performer } from './performers.data';
import { OnsiteProgram, OnsiteActivity } from './onsite.data';
import { JustTime, AllSameKeyValuesEnforcer, sortByProp, str2Date, time_to_min, getDefaultTimeZoneDetails, date2str, getUTCOffset, makeProgramText, enumKeyValuesMatch, sortByPropOfNumber, removeUndefinedsInPlace } from '../helpers/helpers';
import { JoiFile } from './files.model';
import { InformableSectionItem } from './informables.data';
import { Cue, Label, Ranking, Venue } from './events.data';
import { WorkerWithProfile } from './workforce.data';
import { FilterGroup } from './filters.data';
import { JoiEvent } from '../store/events.data';
import { JoiComment } from './tasks.data';
import { DAY_MS } from '../helpers/constants';
import { Asset } from './assets.data';
import { timeHelper } from './../helpers/time.helper';
import { PermissionAccess } from './permissions.data';
import { PublicTimelineItem } from './public.model';
import { ExhibitorStand } from './exhibitors.data';
import { BrandingData } from './publish.data';
import { Attendee } from './attendees.data';
import { Sponsor } from './sponsors.data';
import { Dictionary } from "./app.data";

// THIS IS A PUBLIC FILE!!!  SO DON'T IMPORT FROM outside of data like files

export interface ProgramTabs { setup, basics, exhibitorStands, tagging, assets }
export const programTabs: AllSameKeyValuesEnforcer<ProgramTabs> = { setup: 'setup', basics: 'basics', exhibitorStands: 'exhibitorStands', tagging: 'tagging', assets: 'assets' }

export interface SessionTabs { info, notes, assets, config, logistics, quickNotes, tagging, stagingRequests, catering, cues, facilitator } // quick notes
export const sessionTabs: AllSameKeyValuesEnforcer<SessionTabs> = { info: 'info', notes: 'notes', assets: 'assets', config:'config', logistics: 'logistics', quickNotes: 'quickNotes', tagging: 'tagging', stagingRequests: 'stagingRequests', catering: 'catering', cues: 'cues', facilitator: 'facilitator' }

export const inheritableFields = new Set(['date','venueId', 'timeZoneDetails','secondaryTimeZoneDetails']);

export type SessionType = 'session' | 'break' | 'day'

export interface EntityAsset extends JoiFile {
  submitterId: string
  created: any
}

export const PROGRAM_NAMES = {text:'room or location',  btn:'Room/Location', btn_lower: 'room/location', btn_upper: 'ROOM/LOCATION', btn_plural:'Rooms/Locations'}

export interface Itinerary {
  date: Date;
  time: string;
  title: string;
  location: string;
  venue: string;
  address: string;
  expectedNumber: number;
}

export interface CueAsset {
  assetId?: string
  cueId?: string
  cueIds?: string[]
}


export interface CueRow {
  action?: string
  point?: string
  duration?: JustTime
  cues: {[id: string]: CueAsset}
}

export interface CueSheet { // need to create as an object in case add title / etc
  rows: CueRow[]
}

interface BaseSession {
  start?: string;
  end?: string;
  startForStz?: SecondaryTimeZoneData;
  endForStz?: SecondaryTimeZoneData;
  title: string;
  description?: string;
  performers?: Array<Performer>;
  performerIds?: string[]
  sponsorIds?: string[]
  resourceIds?: string[]
  performerStatus?: { [performerId: string]: string }
  tags?: string[]
  groupTags?: string[]
  cues?: CueSheet
}

// The sad state of these data structures is because of whoever created them on the fly without even thinking about what they were doing (especially when they got committed forever via snapshots)
export interface ProgramTimelineSingleSession extends BaseSession {
  class?: 'group' | 'group-start'
  sessionId: string
  timezoneAbbr: string
  utc: any
  pID: string
  program_title
  programText: string
  venueId: string
  has_venue: boolean
  expanded: 'collapse'
  location: string
  sess_labels: string[]
  prog_labels: string[]
  performerRoleIds: Dictionary<string>
}

export interface ProgramTimelineMultiSession extends BaseSession {
  sessionId: string
  sess_labels: string[]
  timezoneAbbr: string
  utc: any
  pID: string
}

export interface SessionContentStati {"Not Confirmed", "In Progress", "Confirmed", "Review" }
export type SessionContentStatus = keyof SessionContentStati

export interface Session extends BaseSession {
  id: string;
  duration?: number;
  type: SessionType;
  
  on_day?: number;
  
  contentConfirmed?: boolean;
  contentStatus?: SessionContentStatus;
  facilitatorNotes?: string;
  presentationNotes?: InformableSectionItem[]
  presentationAssets?: Asset[]
  otherAssets?: Asset[]
  
  performerTags?: string[]
  sponsorTags?: string[]
  exhibitorTags?: string[]
  resourceTags?: string[]
  attendeeTags?: string[]

  sponsorStatus?: { [sponsorId: string]: string }
  performerBriefStatus?: { [performerId: string]: string }
  includeAddAssetLinkWhenActive?: { [performerId:string]: boolean }
  stagingRequests?: EntityRequest[]

  labels?: Array<string>;

  publicLinks?: Array<JoiFile>
  top?: number;
  height?: number;
  excessive?: string;
  scheduleId?: string;
  time?: string

  quickNotes?: JoiComment[];
  flag?: boolean;

  hidePerformers?: boolean // for display purposes only; a timeline hack
  hideSponsors?: boolean // ditto

  assetIds?: string[]
  presentationAssetIds?: string[]
  assetSessionTitle?: string // for asset display purposes only
  assetDate?: number // for asset display purposes only
  assetSubTitle?: string  // for asset display purposes only

  // staging
  notes?: string;
  stagingItems?: InformableSectionItem[]
  files?: Array<JoiFile>;

  // catering
  cateringNotes?: string
  cateringItems?: InformableSectionItem[]
  cateringFiles?: JoiFile[]

  hideInPublish?: boolean
  performerRoles?: Dictionary<string>

}

export type CueSession = Pick<Session, 'id' | 'cues' | 'start' | 'end' | 'duration' | 'title' | 'type'>
export const SessionToCueSession = ({ id, cues, start, end, duration, title, type }: Session): CueSession =>
  removeUndefinedsInPlace({ id, cues, start, end, duration, title, type})

export interface ManageProgramData { // had to be moved here to avoid circular dependency
  programId: string,
  fromOss?: boolean,
  focusTitle?: boolean,
  openTab?: keyof ProgramTabs
}


export interface TickSession {
  id: string;
  tick: number;
  timeString: string;
  title: string;
  duration: number;
  start?: JustTime;
  end?: JustTime;
  topOffset?: number;
  top?: number;
  height?: number;
  minHeight?: number;
  ratio?: number;
  type?: string;
}



export interface SessionInfo {
  programId: string
  id: string
  title: string
  programTitle: string
  start: string
  end: string
  date: Date
  day: string
  duration?: number
  selected?: boolean // only for selector dialog
  performerInfos: SessionPerformerInfo[] // filtered for currently existing performers
  performerStatus: { [performerId: string]: string }
  performerBriefStatus: { [performerId: string]: string }
  contentStatus: SessionContentStatus
  sessionLabels: string[]
  programLabels: string[]
  description: string
  performers?: Performer[] // for use in the manage performer session dialog
  performerIds?: string[]
  sponsors?: Sponsor[] // for use in the manage sponsor session dialog
  sponsorStatus: { [sponsorId: string]: string }
  sponsorInfos: SessionSponsorInfo[]
  unusedPerformers?: Performer[]
  unusedSponsors?: Sponsor[] 
  fullSession?: Session
  quickNotes?: JoiComment[]
  flag?: boolean
  attendeeTags: string[]

  dropdownSelection?: DropdownOption
  dropdownSelections?: DropdownOption[]

}

export interface SessionPerformerInfo {
  performerId: string
  status: string
  briefStatus: string
}

export interface SessionSponsorInfo {
  sponsorId: string
  status: string
}

export interface DateSchedule {
  id: string;
  date: any;
  programs?: OnsiteProgram[];
  activities?: Array<any>;
  schedules?: OnsiteProgram[];
  ticks?: Array<any>;
  tickSessions?: Array<TickSession>;
  hasConflict?: boolean;
  osPrograms?: Array<any>;
}

export interface ProgramDay {
  day: number;
  start: string;
  end?: string;
  startForStz?: SecondaryTimeZoneData;
  endForStz?: SecondaryTimeZoneData;
  date?: string;
  sessions: Array<string>;
}

export interface Setup {
  id: string
  name: string
  assetIds: string[]
  sessionIds: string[]
  setup?: string
  expected?: number
  capacity?: number
  notes?: InformableSectionItem[]
  access?: JustTime // time
  clear?: JustTime // time
  accessDay?: number // 0 for first day of program, 1 for second (or 1 day after if only 1 day, etc), -1 for one day before, etc
  clearDay?: number // same as above
  date?: number
}

export interface BaseProgram {
  title: string
  location?: string;
  locationConfirmed: boolean;
  venueId: string;
  venueConfirmed: boolean;
  programConfirmed: boolean;
  dateConfirmed: boolean;
  expectedNumber?: number;
  setup?: string;
  setup_items: InformableSectionItem[];

  setupIds?: string[]
  setups?: { [setupId: string]: Setup }
}



export interface PlaceResult {
  name: string
  formatted_address: string
  place_id: string
  latitude: number
  longitude: number
  googleId?: string
}

export interface TimeZoneDaylightDetails {
  offset?: number
  abbr?: string
  offsetText?: string
}

export interface TimeZoneDetails extends TimeZoneDaylightDetails{
  timeZoneId: string
  days: TimeZoneDaylightDetails[]
}

export type TimezoneFetcher = (date: Date) => TimeZoneDetails

export interface VenueSelection {
  venue: string
  address: string
  id: string // currently should be set to program id
  displayedTitle: string // different from title because there can be duplicate venue selections by title
}

export interface EntityRequest {
  by: string
  by_uid?: string
  created?: number
  modified?: number
  type: 'performer' | 'anyone' | 'exhibitor'
  requestText: string
}

export interface DropdownOption<T = string> {
  id: T
  title: string
  italic?: boolean
  dim?: boolean
  font?: string
}

export interface SearchOption<T = string> extends DropdownOption<T> {
  searchTerms: string[]
}

export interface SecondaryTimeZoneData {
  time: string;
  dayOffset?: string;
}



export interface Program extends BaseProgram {
  id: string
  eventId?: string;
  assetIds?: string[]
  sessions?: Array<ProgramDay>;
  sessions_obj:  {[key:string]: Session};
  activities?: Array<OnsiteActivity>;
  id_seed: number;
  order: number;
  days: number;
  labels?: string[];
  date?: string;
  pickedDate?: Date // not for saving, only for use in manage program
  facilitatorNotes?: string;
  files?: Array<JoiFile>;
  createdAt?: any;
  updatedAt?: any;
  label_template?: any // <== lazy solution for the hacked object passed into viewProgram from a cached program timeline
  performerTags?: string[]
  sponsorTags?: string[]
  workforceTags?: string[]
  exhibitorTags?: string[]
  resourceTags?: string[]
  attendeeTags?: string[]
  timeZoneDetails?: TimeZoneDetails
  secondaryTimeZoneDetails: TimeZoneDetails
  cueCols?: Ranking[]
  hiddenCueCols?: string[]
}

export type CueProgram = Pick<Program, 'sessions_obj' | 'id' | 'date' | 'title' | 'sessions' | 'cueCols' | 'hiddenCueCols' | 'secondaryTimeZoneDetails'>
export const ProgramToCueProgram = ({ id, date, title, sessions, sessions_obj: ss, cueCols, hiddenCueCols, secondaryTimeZoneDetails }: Program): CueProgram => {
  const sessions_obj: { [key: string]: CueSession } = {}
  Object.entries(ss).forEach(([k, v]) => sessions_obj[k] = SessionToCueSession(v))
  return { id, date: date || null, title, sessions, sessions_obj, cueCols, hiddenCueCols, secondaryTimeZoneDetails }
}

export interface CueShareData {
  eventTitle: string
  program: CueProgram
  assets: Dictionary<Asset>
  cues: Dictionary<Cue>
  brandingData: BrandingData
}


export type AttendeeSession = Pick<Session, 'id' | 'start' | 'end' | 'duration' | 'title' | 'type' | 'attendeeTags'>
export const SessionToAttendeeSession = ({ id, attendeeTags, start, end, duration, title, type }: Session): AttendeeSession =>
  removeUndefinedsInPlace({ id, attendeeTags, start, end, duration, title, type})

export type AttendeeProgram = Pick<Program, 'id' | 'date' | 'title' | 'sessions' | 'secondaryTimeZoneDetails'>  & { sessions_obj: Dictionary<AttendeeSession> }  
export const ProgramToAttendeeProgram = ({ id, date, title, sessions, sessions_obj: ss, secondaryTimeZoneDetails }: Program): AttendeeProgram => {
  const sessions_obj: { [key: string]: AttendeeSession } = {}
  Object.entries(ss).forEach(([k, v]) => sessions_obj[k] = SessionToAttendeeSession(v))
  return { id, date: date || null, title, sessions, sessions_obj, secondaryTimeZoneDetails }
}

export interface AttendeesViewData {
  eventTitle: string
  programs: AttendeeProgram[]
  attendees: Dictionary<Attendee>
  brandingData: BrandingData
}


export interface ProgramWithVenue extends Program {  venue: string, address: string, latitude: number, longitude: number } // to be compatible with extraction scripts for "old" programs with venues (some are old, some are intermediary as of late)

export interface ProgramTimelineSingleColumn {
  id: string
  date: number
  sessions: ProgramTimelineSingleSession[]
}

export interface ProgramTimelineMultiColumn {
  id: string
  date: number
  programs: ProgramTimelineMultiColumnProgram[]
}

export interface ProgramTimelineMultiColumnProgram extends BaseProgram {
  pID: string
  prog_labels: string[]
  sessions: ProgramTimelineMultiSession[]
}


export interface ProgramTimelineColumns {
  single_column: ProgramTimelineSingleColumn[]
  multiple_column: ProgramTimelineMultiColumn[]
}

export interface ProgramTimelinePerformerData {
  performerIdsBySessionIds: { [sessionId: string]: string[] }
  performerStatusBySessionIds: { [sessionId: string]: string }
  performers?: { [performerId: string]: Performer }
}

export interface ProgramTimelineData extends ProgramTimelineColumns, ProgramTimelinePerformerData {
  title?: string
  showSnapshot?: boolean
}


export interface ProgramTimelineSnapshotNestedData {
  program: Label[]
  session: Label[]
  workersByEmail: { [key: string]: WorkerWithProfile }
}

export interface ProgramTimelineSnapshot {
  columns: ProgramTimelineColumns
  template: ProgramTimelineSnapshotNestedData
  timelineFilters: FilterGroup
  tempTimelineFilterCount: number
  performerData: ProgramTimelinePerformerData
  program_legend?: { [pId: string]: ProgramTimelineMultiColumnProgram } // Not saved to firebase
}

export type SnapshotType = 'revenue' | 'costs' | 'summary'
export interface SnapshotPostData<S> {
  eventId: string
  shotId: string
  snapshot: S
  type?: SnapshotType
}

export function getEmbeddedMapLinkFromVenue(venueObject: Venue, apiKey: string): string {
  if (!venueObject) return null
  const { latitude, longitude, address, venue } = venueObject
  if (!latitude || !longitude || !address || !venue) return null
  return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(address)}&center=${latitude},${longitude}`
}

export const getLinkableVenues = (event: JoiEvent): Venue[] => {
  const venues: Venue[] = Object.values(event ? event.venues || {} : {}).filter(v => v.venue && v.googleId)
  venues.sort(sortByProp('venue'))
  return venues
}

export const getTimeFromProgramSession = (p: Program, s: Session) => 
  !p.date ? s.on_day * DAY_MS : str2Date(p.date).getTime() + (+s.on_day - 1) * DAY_MS + time_to_min(s.start) * 60000

export const getProgramDates = (programs: Partial<Program>[]) => {
  const dateArray: string[] = []
  const addIfMissing = (dateString: string) => !dateArray.includes(dateString) && dateArray.push(dateString)
  programs.filter(p => p.date).map(p => (p.sessions || []).map((_, k) => 
    date2str(new Date(new Date(p.date).getTime() + k * 24 * 60 * 60 * 1000))).forEach(addIfMissing)
  )
  dateArray.sort()
  return dateArray
}

export const initSession = (program: Partial<Program>, type: SessionType = 'session', partialSession: Partial<Session> = null): Session => {
  const on_day = program.sessions.slice(-1)[0].day
  const title = type === 'break' ? 'Out-of-session' : ''
  if(!program.hasOwnProperty('id_seed')) program.id_seed = 0
  const id = program.id+'_'+program.id_seed++
  const session = { duration: 30, id, on_day, title, type } as Session
  return partialSession ? { ...session, ...partialSession } : session
}

export interface ProgramLaneInitDayData {program: Program, day: ProgramDay}

export const initDay = (passedProgram: Program, importedSessions: Partial<Session>[] = null): ProgramLaneInitDayData => {
  const program = JSON.parse(JSON.stringify(passedProgram)) as Program
  if (!program.sessions) program.sessions = []
  if (!program.sessions_obj) program.sessions_obj = {}
  if (!program.days) program.days = 0
  program.days++
  const { days } = program
  const day = { day: days, end:'', sessions:[], start:'09:00' }
  program.sessions.push(day)
  console.log('Initializing day', { structuredData: true, program, importedSessions })
  if (!importedSessions?.length) {
    const first_session: Session = initSession(program);
    day.end = first_session.end;
    day.sessions.push(first_session.id);
    program.sessions_obj[first_session.id] = first_session;
  } else {
    importedSessions.forEach(s => {
      const session = initSession(program, 'session', s)
      day.sessions.push(session.id)
      program.sessions_obj[session.id] = session
    })
    day.end = program.sessions_obj[day.sessions[day.sessions.length - 1]].end
  }
  return { program, day };
}

export const createProgramFoundation = (program: Partial<Program>, renameSessions: boolean, eventId: string, orderNumber: number, defaultTimezoneDetails: TimeZoneDetails, id: string, importedSessions: Partial<Session>[] = null) => {
  if (!program.eventId) program.eventId = eventId
  if (!program.id) program.id = id
  if (!program.hasOwnProperty('order')) program.order = orderNumber
  if (!program.id_seed) program.id_seed = 0
  console.log('here')
  if (!program.sessions) program = initDay(program as Program, importedSessions).program
  console.log({program, renameSessions})
  if (!program.timeZoneDetails) program.timeZoneDetails = defaultTimezoneDetails
  if(renameSessions) {
    program.id_seed = 0
    const new_sessions_days = [];
    const new_sessions_obj = {};
    program.sessions.forEach(day=>{
        const new_sessions = [];
        const new_session_day = {sessions:[]};
        for(const k in day){ if(k !== 'sessions') new_session_day[k] = day[k]; }
        day.sessions.forEach(old_session_id=>{
            const session_id = program.id+'_'+program.id_seed++;
            new_sessions_obj[session_id] = Object.assign({},program.sessions_obj[old_session_id],{id:session_id});
            new_sessions.push(session_id);
        })
        new_session_day.sessions = new_sessions;
        new_sessions_days.push(new_session_day);
    })
    program.sessions_obj = new_sessions_obj;
    program.sessions = new_sessions_days;
  }
  if (importedSessions) {

  }

  return program as Program
}

const MAX_DURATION_MINUTS = 1439
export const updateSessionTimesInPlace = (program: Partial<Program>) => {

  if (!program.sessions) return null
  const offset = program.date && program.secondaryTimeZoneDetails ? program.timeZoneDetails.offset : 0
  const today = new Date()
  program.sessions.forEach((day, d) => {
    day.day = d + 1
    const start = timeHelper.getMinutes(day.start);
    day.start = timeHelper.getTwentyFour(start);
    const startOfDayTime = d * DAY_MS + (program.date ? str2Date(program.date).getTime() : today.getTime() + d * DAY_MS)
    const endOfDayTime = startOfDayTime + DAY_MS - 1000 
    const programStartTime = startOfDayTime + start * 60 * 1000
    if (offset) day.startForStz = timeHelper.getSecondaryTimeData(programStartTime, offset, program.secondaryTimeZoneDetails.timeZoneId, startOfDayTime, endOfDayTime)
    
    const len = day.sessions.length;
    let accumulate = start;
    let is_excessive = false;
    let stzObj = day.startForStz = offset ? timeHelper.getSecondaryTimeData(programStartTime, offset, program.secondaryTimeZoneDetails.timeZoneId, startOfDayTime, endOfDayTime) : null;
    if(!program.timeZoneDetails.days) program.timeZoneDetails.days = [];
    program.timeZoneDetails.days[d] = getUTCOffset(program.timeZoneDetails.timeZoneId, new Date(startOfDayTime) )
    if(offset){
      if(!program.secondaryTimeZoneDetails.days) program.secondaryTimeZoneDetails.days = [];
      program.secondaryTimeZoneDetails.days[d] = getUTCOffset(program.secondaryTimeZoneDetails.timeZoneId, new Date(startOfDayTime));
    } 
    for (let i = 0; i < len; i++) {
      const session_id = day.sessions[i];
      let duration = program.sessions_obj[session_id].duration = program.sessions_obj[session_id].duration ? parseInt(program.sessions_obj[session_id].duration.toString(), 10) : 0;
      program.sessions_obj[session_id].start = timeHelper.getTwentyFour(accumulate);
      if(offset) program.sessions_obj[session_id].startForStz = stzObj;

      if(program.sessions_obj[session_id].excessive) delete program.sessions_obj[session_id].excessive
      if (accumulate + duration > MAX_DURATION_MINUTS) {
          duration = MAX_DURATION_MINUTS- accumulate;
          program.sessions_obj[session_id].duration = duration;
          program.sessions_obj[session_id].excessive = is_excessive ? 'start' : 'end';
          is_excessive = true;
      }
      if (!isNaN(duration) && duration > 0) {
          accumulate += duration;
      }
      program.sessions_obj[session_id].end = timeHelper.getTwentyFour(accumulate);
      program.sessions_obj[session_id].on_day = day.day;
      const end = timeHelper.getMinutes(program.sessions_obj[session_id].end)
      const sessionEndDate = startOfDayTime + end * 60 * 1000
      if(offset) program.sessions_obj[session_id].endForStz = stzObj = timeHelper.getSecondaryTimeData(sessionEndDate, offset, program.secondaryTimeZoneDetails.timeZoneId, startOfDayTime, endOfDayTime);

    }
    // console.log({ accumulate});
    day.end = timeHelper.getTwentyFour(accumulate);
    if(offset) day.endForStz = stzObj;
  })
  program.days = program.sessions.length
  return null
}

export const hasProgramPermissions = (event: JoiEvent, userId: string, { types }: PermissionAccess) => {
  const hasPermission = event && !event.closed && (event.owner_uid === userId || types.includes('program'))
  return hasPermission
}

export const loadProgramTexts = (timeline: PublicTimelineItem[][], excludedFieldIds: string[], venues: {[key:string]:Venue}) => {
  if (!timeline) return {}
  const programTexts = {}
  if (excludedFieldIds?.includes('program')) return programTexts
  timeline.forEach(day => {
    day.filter(s => s.program).forEach(session => {
      const { id, location, title, venueId } = session.program
       if (!programTexts[id]) {
          const venue = !venues ? {} : venues[venueId] || {}
          programTexts[id] = makeProgramText(title, location, venue)
      }
    })
  })
  return programTexts;
}

export enum ProgramLogisticsDisplayOptions { // DON'T change after adding, these are ids
  eventTitle = 'eventTitle',
  madeWithJoi = 'madeWithJoi',
  viewName = 'viewName',
  viewDescription = 'viewDescription',

  roomTab = 'roomTab',
  venueDetails = 'venueDetails',
  roomExhibitors = 'roomExhibitors',
  roomTags = 'roomTags',
  duration = 'duration',
  roomDate = 'roomDate',
  assets = 'assets',

  setupTab = 'setupTab',
  expectedNumbers = 'expectedNumbers',
  capacity = 'capacity',
  roomSetup = 'roomSetup',
  access = 'access',
  clear = 'clear',
  setupNotes = 'setupNotes',
  setupFiles = 'setupFiles',
  setupAssets = 'setupAssets',
  setupSessions = 'setupSessions',

  sessionsTab = 'sessionsTab',
  sessionDescription = 'sessionDescription',
  stagingNotes = 'stagingNotes',
  stagingItems = 'stagingItems',
  stagingFiles = 'stagingFiles',
  cateringNotes = 'cateringNotes',
  cateringItems = 'cateringItems',
  cateringFiles = 'cateringFiles',
  stagingRequests = 'stagingRequests',
  performerBriefNotes = 'performerBriefNotes',
  publicLinks = 'publicLinks',
  performers = 'performers',
  sponsors = 'sponsors',
  sessionTags = 'sessionTags',
  presentationAssets = 'presentationAssets',

}
enumKeyValuesMatch(ProgramLogisticsDisplayOptions);

// Dictionary was replaced with {[key: string]: T} to avoid ts conflicts, but this is the same thing
export const getExhibitorStandIdsByProgramId = (stands: {[id: string]: ExhibitorStand}): {[id: string]: string[]} => {
  const exhibitorStandsByProgramId: {[id: string]: ExhibitorStand[]} = {}
  Object.values(stands).filter(s => s.programId ).forEach((stand) => {
    if (!exhibitorStandsByProgramId[stand.programId]) exhibitorStandsByProgramId[stand.programId] = []
    exhibitorStandsByProgramId[stand.programId].push(stand)
  })
  const exhibitorStandIdsByProgramId: {[id: string]: string[]} = {}
  Object.keys(exhibitorStandsByProgramId).forEach(programId => 
    exhibitorStandIdsByProgramId[programId] = exhibitorStandsByProgramId[programId].sort(sortByProp('programIndex')).map(e => e.id)
  )
  return exhibitorStandIdsByProgramId
}

export const sortPrograms = <T extends Program | OnsiteProgram>(programOrder, allPrograms: T[]): T[]  => {

  if (!programOrder?.length) return allPrograms.sort(sortByPropOfNumber('order'))
  const programs = programOrder.map(id => allPrograms.find(p => p.id === id)).filter(p => !!p)
  allPrograms.filter(p => !programOrder.includes(p.id))
             .forEach(p => programs.push(p)) // make sure all programs added, even if at end

  return programs
}

export const sortPidOrderArray = <T extends { pId: string, order: number }>(programOrder, allPrograms: T[]): T[]  => {
  if (!programOrder?.length) return allPrograms.sort(sortByPropOfNumber('order'))
  const programs = programOrder.map(id => allPrograms.find(p => p.pId === id)).filter(p => !!p)
  allPrograms.filter(p => !programOrder.includes(p.pId))
             .forEach(p => programs.push(p)) // make sure all programs added, even if at end
  return programs
}


export const standardizeProgram = (program: Program): Program => {
  Object.values(program.sessions_obj).forEach(session => {
    if (!session.sponsorIds) session.sponsorIds = []
    if (!session.files) session.files = []
    if (!session.sponsorStatus) session.sponsorStatus = {}
    if (!session.performerIds) session.performerIds = []
    if (!session.performerStatus) session.performerStatus = {}
    if (!session.performerBriefStatus) session.performerBriefStatus = {}
    if (!session.stagingItems) session.stagingItems = []
    if (!session.cateringItems) session.cateringItems = []
    if (!session.cateringFiles) session.cateringFiles = []
    if (!session.cues) session.cues = { rows: [{ cues: {} }] }
    if (!session.contentStatus && session.contentConfirmed) session.contentStatus = "Confirmed";

    session.cues.rows.filter(r => !r.cues).forEach(r => r.cues = {})
    session.cues.rows.forEach(r => {
      Object.values(r.cues).forEach(cueAsset => {
        if (cueAsset.cueId && !cueAsset.cueIds) cueAsset.cueIds = [cueAsset.cueId]
        else if (!cueAsset.cueIds) cueAsset.cueIds = []
      })
    })


    if (!session.presentationNotes) session.presentationNotes = []
    if (!session.performerRoles) session.performerRoles = {}

  })

  if (!program.timeZoneDetails) program.timeZoneDetails = getDefaultTimeZoneDetails(program.sessions.length)
  // console.log({ venue, address, venueHash, newVenue })
  if (!program.setup_items) {
    program.setup_items = []
  } else if (program.setup_items && program.setup_items.length && !program.setup_items[0].hasOwnProperty('name')) {
    program.setup_items = program.setup_items.map(({ id, title, note, files }: any) => {
      const setupItem: InformableSectionItem = { id, name: title, placeholder: 'Add setup item...', description: note, files }
      return setupItem
    })
  }
  if (!program.setups) {
    const notes: InformableSectionItem[] = program.setup_items ? [...program.setup_items] : []
    if (program.files?.length) notes.unshift({
      name: `${program.title} files`,
      files: program.files,
      id: 'originalFiles',
      description: `Original files of ${program.title}`,
      placeholder: null,
    })


    program.setups = {}
    program.setupIds = ['default']
    program.setups['default'] = {
      id: 'default',
      sessionIds: [],
      expected: program.expectedNumber || null,
      capacity: null,
      clearDay: 0,
      accessDay: 0,
      name: `${program.title} setup`,
      notes,
      setup: program.setup || null,
      assetIds: []
    }
  } else if (!program.setupIds) {
    program.setupIds = Object.keys(program.setups)
  }

  if (!program.cueCols) program.cueCols = [
    { id: 'vision',  name: 'Vision', rank: 0 },
    { id: 'audio',  name: 'Audio', rank: 1 },
    { id: 'lighting',  name: 'Lighting', rank: 2 },
    { id: 'stage',  name: 'Stage', rank: 3 }
  ]

  return program
}


export interface ManageSessionData {
  sessionId: string
  openTab?: keyof SessionTabs
}
